import React from 'react';
import { type Href, Link } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl, type NoMessageValues } from 'react-intl';
import { StyleSheet, Text } from 'react-native';

import { theme } from '@garnish/constants';
import { CustomNavigationHeader } from '@garnish/navigation';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderLocationHeader = (props: Props) => {
  const { orderChannel, locationName, onClose } = props;

  const { formatMessage } = useIntl();

  return (
    <CustomNavigationHeader.Container transition={null} safeAreaEdges={null}>
      <CustomNavigationHeader.Text textAlignment="center" numberOfLines={1}>
        <Link href={LOCATION_SELECTION_HREF}>
          <Text style={styles.link}>
            <FormattedMessage
              {...messages.orderChannelText}
              values={{ order_channel: orderChannel }}
            />
          </Text>
        </Link>

        <Text>
          <FormattedMessage
            {...messages.orderChannelSucceedingText}
            values={{ order_channel: orderChannel }}
          />
        </Text>

        <Link href={LOCATION_SELECTION_HREF}>
          <Text style={styles.link}>{locationName}</Text>
        </Link>
      </CustomNavigationHeader.Text>

      <CustomNavigationHeader.Button.Right
        name="IconClose"
        accessibilityLabel={formatMessage(messages.closeButtonA11yLabel)}
        onPress={onClose}
      />
    </CustomNavigationHeader.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly orderChannelText: { readonly order_channel: string };
  readonly orderChannelSucceedingText: { readonly order_channel: string };
  readonly closeButtonA11yLabel: NoMessageValues;
}>({
  orderChannelText: {
    defaultMessage: `{order_channel, select,
      pickup {Pickup}
      delivery {Delivery}
      outpost {Outpost}
      other {}
    }`,
    description: 'Reorder | Location Header | Text | Order channel',
  },
  orderChannelSucceedingText: {
    defaultMessage: `{order_channel, select,
      pickup { from }
      delivery { to }
      outpost { at }
      other { }
    }`,
    description: 'Reorder | Location Header | Text | Order channel succeeding text',
  },
  closeButtonA11yLabel: {
    defaultMessage: 'Close',
    description: 'Reorder | Location Header | Close button | a11y label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATION_SELECTION_HREF = {
  pathname: '/location-selection',
  params: { backToPath: '/reorder' },
} satisfies Href;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    color: theme.colors.SPINACH,
    textDecorationLine: 'underline',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  orderChannel: 'pickup' | 'outpost' | 'delivery';
  locationName: string;
  onClose: () => void;
};
